// src/pages/admin/AdminSupport.tsx

import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import "./AdminSupport.css";


// =====================================================
// API
// =====================================================

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";


// =====================================================
// TYPES
// =====================================================

interface SupportStats {
  total: number;
  new: number;
  inProgress: number;
  waitingForUser: number;
  resolved: number;
  closed: number;
  urgent: number;
  contacts: number;
  problems: number;
}


interface SupportTicket {
  id: string;
  ticket_number: string;
  user_id: string | null;
  ticket_type: "contact" | "problem";
  status:
    | "new"
    | "in_progress"
    | "waiting_for_user"
    | "resolved"
    | "closed";
  priority:
    | "low"
    | "normal"
    | "high"
    | "urgent";
  subject: string;
  requester_name: string | null;
  requester_email: string | null;
  assigned_admin_user_id: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  closed_at: string | null;
  requester_username: string | null;
  registered_user_name: string | null;
  assigned_admin_username: string | null;
  assigned_admin_name: string | null;
  reply_count: number;
}


interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}


interface StatsResponse {
  success: boolean;
  stats?: SupportStats;
  message?: string;
}


interface TicketsResponse {
  success: boolean;
  tickets?: SupportTicket[];
  pagination?: Pagination;
  message?: string;
}


// =====================================================
// DEFAULT STATS
// =====================================================

const EMPTY_STATS: SupportStats = {
  total: 0,
  new: 0,
  inProgress: 0,
  waitingForUser: 0,
  resolved: 0,
  closed: 0,
  urgent: 0,
  contacts: 0,
  problems: 0,
};


// =====================================================
// HELPERS
// =====================================================

const formatStatus = (
  status: SupportTicket["status"]
) => {

  switch (status) {

    case "new":
      return "New";

    case "in_progress":
      return "In Progress";

    case "waiting_for_user":
      return "Waiting for User";

    case "resolved":
      return "Resolved";

    case "closed":
      return "Closed";

    default:
      return status;
  }
};


const formatPriority = (
  priority: SupportTicket["priority"]
) => {

  return (
    priority.charAt(0).toUpperCase() +
    priority.slice(1)
  );
};


const formatTicketType = (
  type: SupportTicket["ticket_type"]
) => {

  return type === "problem"
    ? "Problem Report"
    : "Contact";
};


const formatDate = (
  date: string
) => {

  const value =
    new Date(date);

  if (
    Number.isNaN(
      value.getTime()
    )
  ) {
    return "—";
  }

  return value.toLocaleString(
    undefined,
    {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }
  );
};


// =====================================================
// ADMIN SUPPORT
// =====================================================

const AdminSupport: React.FC = () => {

  const navigate =
    useNavigate();


  // ===================================================
  // STATE
  // ===================================================

  const [
    stats,
    setStats,
  ] = useState<SupportStats>(
    EMPTY_STATS
  );


  const [
    tickets,
    setTickets,
  ] = useState<SupportTicket[]>(
    []
  );


  const [
    pagination,
    setPagination,
  ] = useState<Pagination>({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    refreshing,
    setRefreshing,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  const [
    search,
    setSearch,
  ] = useState("");


  const [
    submittedSearch,
    setSubmittedSearch,
  ] = useState("");


  const [
    typeFilter,
    setTypeFilter,
  ] = useState("all");


  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");


  const [
    priorityFilter,
    setPriorityFilter,
  ] = useState("all");


  const [
    page,
    setPage,
  ] = useState(1);


  // ===================================================
  // AUTH FAILURE
  // ===================================================

  const handleAuthenticationFailure =
    useCallback(
      (
        status: number
      ) => {

        if (status === 401) {

          localStorage.removeItem(
            "token"
          );

          navigate(
            "/login",
            {
              replace: true,
              state: {
                returnTo:
                  "/admin/support",
              },
            }
          );

          return true;
        }


        if (status === 403) {

          navigate(
            "/",
            {
              replace: true,
            }
          );

          return true;
        }


        return false;
      },
      [
        navigate,
      ]
    );


  // ===================================================
  // LOAD STATISTICS
  // ===================================================

  const loadStats =
    useCallback(
      async () => {

        const token =
          localStorage.getItem(
            "token"
          );


        if (!token) {

          navigate(
            "/login",
            {
              replace: true,
              state: {
                returnTo:
                  "/admin/support",
              },
            }
          );

          return;
        }


        const response =
          await fetch(
            `${API_BASE_URL}/api/admin/support/stats`,
            {
              method: "GET",

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );


        if (
          handleAuthenticationFailure(
            response.status
          )
        ) {
          return;
        }


        const data:
          StatsResponse =
            await response.json();


        if (
          !response.ok ||
          !data.success ||
          !data.stats
        ) {
          throw new Error(
            data.message ||
            "Unable to load support statistics."
          );
        }


        setStats(
          data.stats
        );
      },
      [
        handleAuthenticationFailure,
        navigate,
      ]
    );


  // ===================================================
  // LOAD TICKETS
  // ===================================================

  const loadTickets =
    useCallback(
      async () => {

        const token =
          localStorage.getItem(
            "token"
          );


        if (!token) {

          navigate(
            "/login",
            {
              replace: true,
              state: {
                returnTo:
                  "/admin/support",
              },
            }
          );

          return;
        }


        const params =
          new URLSearchParams();


        params.set(
          "page",
          String(page)
        );

        params.set(
          "limit",
          "25"
        );


        if (
          statusFilter !== "all"
        ) {
          params.set(
            "status",
            statusFilter
          );
        }


        if (
          typeFilter !== "all"
        ) {
          params.set(
            "type",
            typeFilter
          );
        }


        if (
          priorityFilter !== "all"
        ) {
          params.set(
            "priority",
            priorityFilter
          );
        }


        if (
          submittedSearch.trim()
        ) {
          params.set(
            "search",
            submittedSearch.trim()
          );
        }


        const response =
          await fetch(
            `${API_BASE_URL}/api/admin/support/tickets?${params.toString()}`,
            {
              method: "GET",

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );


        if (
          handleAuthenticationFailure(
            response.status
          )
        ) {
          return;
        }


        const data:
          TicketsResponse =
            await response.json();


        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.message ||
            "Unable to load support tickets."
          );
        }


        setTickets(
          data.tickets || []
        );


        if (data.pagination) {

          setPagination(
            data.pagination
          );

        } else {

          setPagination({
            page,
            limit: 25,
            total:
              data.tickets?.length || 0,
            totalPages: 1,
          });
        }
      },
      [
        page,
        statusFilter,
        typeFilter,
        priorityFilter,
        submittedSearch,
        handleAuthenticationFailure,
        navigate,
      ]
    );


  // ===================================================
  // LOAD DASHBOARD
  // ===================================================

  const loadDashboard =
    useCallback(
      async (
        manualRefresh = false
      ) => {

        try {

          setError("");


          if (manualRefresh) {

            setRefreshing(
              true
            );

          } else {

            setLoading(
              true
            );
          }


          await Promise.all([
            loadStats(),
            loadTickets(),
          ]);

        } catch (requestError) {

          console.error(
            "Unable to load admin support dashboard:",
            requestError
          );


          setError(
            requestError instanceof Error
              ? requestError.message
              : "Unable to load the support dashboard."
          );

        } finally {

          setLoading(
            false
          );

          setRefreshing(
            false
          );
        }
      },
      [
        loadStats,
        loadTickets,
      ]
    );


  // ===================================================
  // INITIAL / FILTER LOAD
  // ===================================================

  useEffect(
    () => {

      loadDashboard();

    },
    [
      loadDashboard,
    ]
  );


  // ===================================================
  // SEARCH
  // ===================================================

  const handleSearchSubmit = (
    event:
      React.FormEvent<HTMLFormElement>
  ) => {

    event.preventDefault();

    setPage(1);

    setSubmittedSearch(
      search.trim()
    );
  };


  // ===================================================
  // CLEAR FILTERS
  // ===================================================

  const clearFilters = () => {

    setSearch("");

    setSubmittedSearch("");

    setTypeFilter("all");

    setStatusFilter("all");

    setPriorityFilter("all");

    setPage(1);
  };


  // ===================================================
  // OPEN TICKET
  // ===================================================

  const openTicket = (
    ticketId: string
  ) => {

    navigate(
      `/admin/support/tickets/${ticketId}`
    );
  };


  // ===================================================
  // UI
  // ===================================================

  return (
    <main className="admin-support-page">

      {/* =========================================== */}
      {/* HEADER                                      */}
      {/* =========================================== */}

      <header className="admin-support-header">

        <div className="admin-support-header-inner">

          <div className="admin-support-brand">

            <Link
              to="/"
              className="admin-support-logo"
            >
              P<span>-ARENA</span>
            </Link>

            <span className="admin-support-divider">
              /
            </span>

            <span className="admin-support-section-name">
              Admin
            </span>

          </div>


          <Link
            to="/"
            className="admin-support-house-link"
          >
            Game House
          </Link>

        </div>

      </header>


      {/* =========================================== */}
      {/* HERO                                        */}
      {/* =========================================== */}

      <section className="admin-support-hero">

        <div className="admin-support-hero-glow" />

        <div className="admin-support-container">

          <div className="admin-support-hero-row">

            <div>

              <p className="admin-support-eyebrow">
                P-ARENA ADMINISTRATION
              </p>

              <h1>
                Support Center
              </h1>

              <p className="admin-support-description">
                Review player questions, investigate
                problem reports, manage priorities,
                and respond to support tickets.
              </p>

            </div>


            <button
              type="button"
              className="admin-support-refresh"
              disabled={
                refreshing
              }
              onClick={() =>
                loadDashboard(
                  true
                )
              }
            >
              {refreshing
                ? "Refreshing..."
                : "Refresh"}
            </button>

          </div>

        </div>

      </section>


      {/* =========================================== */}
      {/* CONTENT                                     */}
      {/* =========================================== */}

      <div className="admin-support-container admin-support-main">

        {/* ========================================= */}
        {/* STATS                                     */}
        {/* ========================================= */}

        <section
          className="admin-support-stats"
          aria-label="Support statistics"
        >

          <article className="admin-stat-card admin-stat-total">

            <span className="admin-stat-label">
              Total Tickets
            </span>

            <strong>
              {stats.total}
            </strong>

            <small>
              All support requests
            </small>

          </article>


          <article className="admin-stat-card admin-stat-new">

            <span className="admin-stat-label">
              New
            </span>

            <strong>
              {stats.new}
            </strong>

            <small>
              Awaiting review
            </small>

          </article>


          <article className="admin-stat-card admin-stat-progress">

            <span className="admin-stat-label">
              In Progress
            </span>

            <strong>
              {stats.inProgress}
            </strong>

            <small>
              Being handled
            </small>

          </article>


          <article className="admin-stat-card admin-stat-waiting">

            <span className="admin-stat-label">
              Waiting
            </span>

            <strong>
              {stats.waitingForUser}
            </strong>

            <small>
              Waiting for player
            </small>

          </article>


          <article className="admin-stat-card admin-stat-resolved">

            <span className="admin-stat-label">
              Resolved
            </span>

            <strong>
              {stats.resolved}
            </strong>

            <small>
              Successfully resolved
            </small>

          </article>


          <article className="admin-stat-card admin-stat-urgent">

            <span className="admin-stat-label">
              Urgent
            </span>

            <strong>
              {stats.urgent}
            </strong>

            <small>
              Needs attention
            </small>

          </article>

        </section>


        {/* ========================================= */}
        {/* SECONDARY STATS                           */}
        {/* ========================================= */}

        <section className="admin-support-summary">

          <div>

            <span>
              Contact Messages
            </span>

            <strong>
              {stats.contacts}
            </strong>

          </div>


          <div>

            <span>
              Problem Reports
            </span>

            <strong>
              {stats.problems}
            </strong>

          </div>


          <div>

            <span>
              Closed
            </span>

            <strong>
              {stats.closed}
            </strong>

          </div>

        </section>


        {/* ========================================= */}
        {/* INBOX                                     */}
        {/* ========================================= */}

        <section className="admin-support-inbox">

          <div className="admin-support-inbox-heading">

            <div>

              <p className="admin-support-mini-label">
                SUPPORT INBOX
              </p>

              <h2>
                Tickets
              </h2>

              <p>
                {pagination.total} support{" "}
                {pagination.total === 1
                  ? "ticket"
                  : "tickets"}
              </p>

            </div>

          </div>


          {/* ======================================= */}
          {/* FILTERS                                 */}
          {/* ======================================= */}

          <div className="admin-support-toolbar">

            <form
              className="admin-support-search"
              onSubmit={
                handleSearchSubmit
              }
            >

              <input
                type="search"
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder="Search ticket, player, email..."
                aria-label="Search support tickets"
              />

              <button type="submit">
                Search
              </button>

            </form>


            <div className="admin-support-filters">

              <select
                value={typeFilter}
                onChange={(event) => {

                  setTypeFilter(
                    event.target.value
                  );

                  setPage(1);
                }}
                aria-label="Filter by ticket type"
              >

                <option value="all">
                  All Types
                </option>

                <option value="contact">
                  Contact
                </option>

                <option value="problem">
                  Problem Reports
                </option>

              </select>


              <select
                value={statusFilter}
                onChange={(event) => {

                  setStatusFilter(
                    event.target.value
                  );

                  setPage(1);
                }}
                aria-label="Filter by status"
              >

                <option value="all">
                  All Statuses
                </option>

                <option value="new">
                  New
                </option>

                <option value="in_progress">
                  In Progress
                </option>

                <option value="waiting_for_user">
                  Waiting for User
                </option>

                <option value="resolved">
                  Resolved
                </option>

                <option value="closed">
                  Closed
                </option>

              </select>


              <select
                value={priorityFilter}
                onChange={(event) => {

                  setPriorityFilter(
                    event.target.value
                  );

                  setPage(1);
                }}
                aria-label="Filter by priority"
              >

                <option value="all">
                  All Priorities
                </option>

                <option value="low">
                  Low
                </option>

                <option value="normal">
                  Normal
                </option>

                <option value="high">
                  High
                </option>

                <option value="urgent">
                  Urgent
                </option>

              </select>


              <button
                type="button"
                className="admin-support-clear"
                onClick={
                  clearFilters
                }
              >
                Clear
              </button>

            </div>

          </div>


          {/* ======================================= */}
          {/* ERROR                                   */}
          {/* ======================================= */}

          {error && (

            <div
              className="admin-support-error"
              role="alert"
            >

              <strong>
                Unable to load support
              </strong>

              <p>
                {error}
              </p>

              <button
                type="button"
                onClick={() =>
                  loadDashboard(
                    true
                  )
                }
              >
                Try Again
              </button>

            </div>

          )}


          {/* ======================================= */}
          {/* LOADING                                 */}
          {/* ======================================= */}

          {loading && !error && (

            <div className="admin-support-loading">

              <div className="admin-support-spinner" />

              <p>
                Loading support tickets...
              </p>

            </div>

          )}


          {/* ======================================= */}
          {/* EMPTY                                   */}
          {/* ======================================= */}

          {!loading &&
            !error &&
            tickets.length === 0 && (

              <div className="admin-support-empty">

                <div className="admin-support-empty-icon">
                  ♣
                </div>

                <h3>
                  No Tickets Found
                </h3>

                <p>
                  There are no support tickets
                  matching the current filters.
                </p>

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                >
                  Clear Filters
                </button>

              </div>

            )}


          {/* ======================================= */}
          {/* DESKTOP TABLE                           */}
          {/* ======================================= */}

          {!loading &&
            !error &&
            tickets.length > 0 && (

              <>

                <div className="admin-support-table-wrapper">

                  <table className="admin-support-table">

                    <thead>

                      <tr>

                        <th>
                          Ticket
                        </th>

                        <th>
                          Requester
                        </th>

                        <th>
                          Type
                        </th>

                        <th>
                          Subject
                        </th>

                        <th>
                          Status
                        </th>

                        <th>
                          Priority
                        </th>

                        <th>
                          Replies
                        </th>

                        <th>
                          Created
                        </th>

                        <th>
                          Assigned
                        </th>

                      </tr>

                    </thead>


                    <tbody>

                      {tickets.map(
                        (ticket) => (

                          <tr
                            key={
                              ticket.id
                            }
                            tabIndex={0}
                            role="button"
                            onClick={() =>
                              openTicket(
                                ticket.id
                              )
                            }
                            onKeyDown={(event) => {

                              if (
                                event.key ===
                                  "Enter" ||
                                event.key ===
                                  " "
                              ) {

                                event.preventDefault();

                                openTicket(
                                  ticket.id
                                );
                              }
                            }}
                          >

                            <td>

                              <div className="admin-ticket-number">

                                <strong>
                                  {
                                    ticket.ticket_number
                                  }
                                </strong>

                                <span>
                                  #{ticket.id}
                                </span>

                              </div>

                            </td>


                            <td>

                              <div className="admin-ticket-requester">

                                <strong>
                                  {
                                    ticket.requester_name ||
                                    ticket.registered_user_name ||
                                    ticket.requester_username ||
                                    "Unknown Player"
                                  }
                                </strong>

                                <span>
                                  {
                                    ticket.requester_email ||
                                    ticket.requester_username ||
                                    "—"
                                  }
                                </span>

                              </div>

                            </td>


                            <td>

                              <span
                                className={`
                                  admin-ticket-type
                                  admin-ticket-type-${ticket.ticket_type}
                                `}
                              >
                                {
                                  formatTicketType(
                                    ticket.ticket_type
                                  )
                                }
                              </span>

                            </td>


                            <td className="admin-ticket-subject">
                              {ticket.subject}
                            </td>


                            <td>

                              <span
                                className={`
                                  admin-ticket-status
                                  admin-ticket-status-${ticket.status}
                                `}
                              >
                                {
                                  formatStatus(
                                    ticket.status
                                  )
                                }
                              </span>

                            </td>


                            <td>

                              <span
                                className={`
                                  admin-ticket-priority
                                  admin-ticket-priority-${ticket.priority}
                                `}
                              >
                                {
                                  formatPriority(
                                    ticket.priority
                                  )
                                }
                              </span>

                            </td>


                            <td>
                              {
                                Number(
                                  ticket.reply_count
                                ) || 0
                              }
                            </td>


                            <td>
                              {
                                formatDate(
                                  ticket.created_at
                                )
                              }
                            </td>


                            <td>

                              {
                                ticket.assigned_admin_name ||
                                ticket.assigned_admin_username ||
                                "Unassigned"
                              }

                            </td>

                          </tr>

                        )
                      )}

                    </tbody>

                  </table>

                </div>


                {/* ================================= */}
                {/* MOBILE CARDS                      */}
                {/* ================================= */}

                <div className="admin-support-mobile-list">

                  {tickets.map(
                    (ticket) => (

                      <button
                        type="button"
                        key={
                          `mobile-${ticket.id}`
                        }
                        className="admin-support-mobile-ticket"
                        onClick={() =>
                          openTicket(
                            ticket.id
                          )
                        }
                      >

                        <div className="admin-mobile-ticket-top">

                          <div>

                            <strong>
                              {
                                ticket.ticket_number
                              }
                            </strong>

                            <span>
                              {
                                formatDate(
                                  ticket.created_at
                                )
                              }
                            </span>

                          </div>


                          <span
                            className={`
                              admin-ticket-status
                              admin-ticket-status-${ticket.status}
                            `}
                          >
                            {
                              formatStatus(
                                ticket.status
                              )
                            }
                          </span>

                        </div>


                        <div className="admin-mobile-ticket-subject">
                          {ticket.subject}
                        </div>


                        <div className="admin-mobile-ticket-requester">

                          <strong>
                            {
                              ticket.requester_name ||
                              ticket.registered_user_name ||
                              ticket.requester_username ||
                              "Unknown Player"
                            }
                          </strong>

                          <span>
                            {
                              ticket.requester_email ||
                              ticket.requester_username ||
                              "—"
                            }
                          </span>

                        </div>


                        <div className="admin-mobile-ticket-meta">

                          <span
                            className={`
                              admin-ticket-type
                              admin-ticket-type-${ticket.ticket_type}
                            `}
                          >
                            {
                              formatTicketType(
                                ticket.ticket_type
                              )
                            }
                          </span>


                          <span
                            className={`
                              admin-ticket-priority
                              admin-ticket-priority-${ticket.priority}
                            `}
                          >
                            {
                              formatPriority(
                                ticket.priority
                              )
                            }
                          </span>


                          <span>
                            {
                              Number(
                                ticket.reply_count
                              ) || 0
                            }{" "}
                            {
                              Number(
                                ticket.reply_count
                              ) === 1
                                ? "reply"
                                : "replies"
                            }
                          </span>

                        </div>

                      </button>

                    )
                  )}

                </div>


                {/* ================================= */}
                {/* PAGINATION                        */}
                {/* ================================= */}

                <div className="admin-support-pagination">

                  <button
                    type="button"
                    disabled={
                      page <= 1
                    }
                    onClick={() =>
                      setPage(
                        (current) =>
                          Math.max(
                            1,
                            current - 1
                          )
                      )
                    }
                  >
                    Previous
                  </button>


                  <span>
                    Page{" "}
                    <strong>
                      {
                        pagination.page
                      }
                    </strong>
                    {" "}of{" "}
                    <strong>
                      {
                        Math.max(
                          pagination.totalPages,
                          1
                        )
                      }
                    </strong>
                  </span>


                  <button
                    type="button"
                    disabled={
                      page >=
                      pagination.totalPages
                    }
                    onClick={() =>
                      setPage(
                        (current) =>
                          current + 1
                      )
                    }
                  >
                    Next
                  </button>

                </div>

              </>

            )}

        </section>

      </div>

    </main>
  );
};


export default AdminSupport;